import { Classified } from '~/models/classified/types'
import {
  NotificationEvent,
  NotificationType
} from '~/models/notification-center/types'
import { AccessToggle } from '~/models/user/access-toggles'
import {
  AuthUserSubscriptionInfo,
  AuthUserStripeSubscriptionInfo
} from '~/models/user/subscription'
import { Pagination } from '~/models/search/types'
import { AdminExtrasState } from '~/store/modules/shared/admin/user/state'
import { SubscriptionPlanName } from '~/services/logistics/StripeService'
import { QuickSearch } from '~/models/quick-search/types'
import { Postcode, Telephone } from '../shared/types'
import { Geolocation } from '../common/types'

export interface AuthUserResult {
  username: string
  name: string
  id: number
  avatar: string | null
  type: UserType
  credits?: UserCredits
  properties: string[]
  permissions: string[]
  location: object
  featureToggles: FeatureToggle[]
  referenceCode?: string
  isPayingUser: boolean
  accessToggles: AccessToggle[]
  subscription?: AuthUserSubscriptionInfo
  stripeSubscription?: AuthUserStripeSubscriptionInfo
  sdmu: boolean
  hasCompleteProfile?: boolean
  dealerIds?: number[]
  hasPublicDealers?: boolean
  externalIds?: object
}

export interface AuditRequestsCount {
  failed: number
  all: number
  completed: number
  pending: number
}

export interface RoqueRequirements {
  hasTelehone: string
  hasAlias: string
  hasPostcode: string
}
export interface UserExtrasResult {
  unreadNotifications: number
  unreadMessages: number
  unreadParkingNotifications: number
  unreadTradesNotifications: number
  myClassifieds?: MyClassifiedsLink[]
  xmlIssues?: boolean | null
  invoicesCount?: number
  outletsCount?: number
  rogueClassifiedsCount?: number
  rogueRequirements?: RoqueRequirements
  auditRequestsCount?: AuditRequestsCount
}

export interface AdminPageGroup {
  name: string
  pages: AdminPage[]
}

export interface AdminPage {
  name: string
  url: string
}

export interface UserAndAdminExtrasResult {
  userExtras: UserExtrasResult
  adminExtrasData?: AdminExtrasState
}

export interface MyClassifiedsLink {
  count: number
  categoryName: string
  categoryIds: number[]
  seoUrl: string
  legacyVtype?: string | null
  selected: boolean
  type: string
  label: string
}

export interface StorageStateResult {
  state: object
}

export interface StorageStatePatchResult extends StorageStateResult {
  patched: boolean
}

export interface User {
  username?: string
  name?: string
  id?: number
  type?: UserType
  credits?: UserCredits
  properties: any
  permissions: string[]
  avatar: string | null
  unreadNotifications: number
  unreadMessages: number
  unreadParkingNotifications: number
  unreadTradesNotifications: number
  auditRequestsCount?: AuditRequestsCount
  myClassifieds?: MyClassifiedsLink[]
  xmlIssues?: boolean | null
  invoicesCount?: number
  outletsCount?: number
  rogueClassifiedsCount?: number
  rogueRequirements?: RoqueRequirements
  hasPublicDealers?: boolean
  referenceCode?: string
  externalIds?: object
  isPayingUser?: boolean
  notificationsCenter: UserNotificationsCenter
  sdmu: boolean
  hasCompleteProfile?: boolean
  dealerIds?: number[]
}

export interface UserNotificationsCenter {
  loading: boolean
  notifications: {
    pagination: Pagination
    rows: Array<NotificationEvent>
  }
  notificationTypes: Array<NotificationType>
}
export interface UserCredits {
  free: number
  paid: number
  freeDaily?: number
}

export enum UserType {
  ADMIN = 'Admin',
  MANAGER = 'Manager',
  DEALER = 'Dealer',
  GUEST = 'Guest',
  SINGLE = 'SingleClassifiedUser',
  AGENT = 'Agent',
  ANON = 'anon',
  AUDIT_PROVIDER = 'AuditProvider',
  AUDIT_SHOP = 'AuditShop',
  AUDITOR = 'Auditor'
}

export enum Type {
  TOP_PICKS = 'top_picks'
}

export enum FeedTypes {
  RECENT = 'recent',
  RECENT_VIEWED = 'recent_viewed',
  LIST = 'classified_list',
  SEARCH = 'search',
  FAVORITES = 'favorites',
  MODELS = 'models_feed',
  TOP_PICKS = 'top_picks',
  CATEGORIES = 'categories_feed',
  YOUTUBE = 'youtube',
  ELECTRIC_VEHICLES = 'electric_vehicles_feed',
  USER_CLASSIFIEDS = 'user_classifieds',
  FAVORITE_USER_SEARCH = 'favorite_user_search',
  FAVORITE_CLASSIFIEDS = 'favorite_classifieds',
  RECENT_USER_SEARCH = 'recent_user_search',
  RECENT_VIEWED_CLASSIFIEDS = 'recent_viewed_classifieds',
  DEALS = 'deals',
  PLOT = 'plot',
  AGENTS_FEED = 'agents_feed',
  CUSTOM_LINK = 'custom_link'
}
export interface LandingResult {
  fetchableFeeds: FetchableFeed[]
  unreadMessages?: number
  parkingNotifications?: number
  quickSearches: QuickSearches
}

export interface QuickSearchesLocations {
  rent: QuickSearch[]
  sale: QuickSearch[]
}

export interface QuickSearches {
  locations: QuickSearchesLocations
}

export interface LandingClassifiedsCount {
  count: number
}

export interface FetchableFeed {
  fetchUrl: string
  type: FeedTypes
  refreshInterval: number | null
  feed?: Feed
  id?: number
  searchType?: string
}

export interface Feed {
  classifieds: Classified[]
  label: object
  type: FeedTypes
  seoUrl?: string
  count?: number
  categoryIds?: number[]
  hasMore?: boolean
}

export interface FeedCategory {
  targetUrl: string
  classifiedsCount: Number | null
  title: string
  image: string
  categoryIds: number[]
  id: number
  targetSearchArgs: TargetSearchArg[]
}

export interface FeedUser {
  classifiedsCount: number
  pageUrl: string
  managerId?: number | null
  address: string
  subscriptionTier: SubscriptionPlanName
  type: UserType
  id: number
  avatar: string | null
  name: string
}

export interface TargetSearchArg {
  name: string
  value: any
}

export interface FeedModel {
  targetUrl: string
  classifiedsCount: Number | null
  title: string
  image: string
  id: number
}

export interface FeedVideo {
  image: string
  targetUrl: string
  title: string
  description?: string
}
export interface CustomLinkEntry extends FeedVideo {}

export interface CategoryFeed {
  categories: FeedCategory[]
  label: object
  type: FeedTypes
  seoUrl: string
  count: number
  icon: string
}
export interface AgentsFeed {
  agents: FeedUser[]
  label: object
  type: FeedTypes
  seoUrl: string
}
export interface ModelsFeed {
  models: FeedModel[]
  label: object
  type: FeedTypes
  seoUrl: string
  categoryIds: number[]
}

export interface YoutubeFeed {
  videos: FeedVideo[]
  label: object
  type: FeedTypes
  seoUrl: string
}
export interface CustomLinkFeed {
  linkData: CustomLinkEntry[]
  type: FeedTypes
  label: object
  seoUrl: string
  icon?: string
}

export interface RecentButton {
  catId: number
  label: string
  active: boolean
}
export interface Verify {
  isVerified: Boolean
  telephone: {
    code: number
    dontShowToPublic: Boolean
    social: { whatsapp: string; viber: string }
    telephone: string
  }
}

export enum VerificationStateEnum {
  ENTER_PIN = 'ENTER_PIN',
  NOT_VERIFIED = 'NOT_VERIFIED',
  VERIFIED = 'VERIFIED',
  ALREADY_VERIFIED = 'ALREADY_VERIFIED'
}

export enum FeatureToggle {
  JOBS = 'jobs',
  SEARCHBAR = 'searchbar'
}

export type UserId = number | string

interface IUserBaseData {
  username: string
  notes: string
  telephone1: Telephone
  telephone2?: Telephone
  postcode: Postcode
  paidCredits?: number
  freeCredits?: number
  realEstateClassifiedLimit?: number
  deleted: boolean
  dailyPaidCredits?: number
  dailyFreeCredits?: number
}
export interface IGuestUserData extends IUserBaseData {
  alias?: string
  firstName?: string
  lastName?: string
  guestCreated: string
  guestLastLogin: string
  priceAlerts?: boolean
  activated: boolean
  classifiedsNonDeleted?: number
  classifiedsDeleted?: number
}
export interface IAgentUserData extends IUserBaseData {
  name: string
  inchargeName?: string
  description?: string
  agentCode?: string
  slogan?: string
  address?: string
  workingHours?: string
  geolocation: Geolocation
  email: string
  technicalEmail?: string
  pagePath: string
  website?: string
  facebook?: string
  twitter?: string
  youtube?: string
  instagram?: string
  agentCreated: string
  agentLastLogin: string
  agentLoginExpiration: string
  classifiedsMessage?: string
  classifiedsNonDeleted?: number
  classifiedsDeleted?: number
  subscriptionExpiration: string
  subscriptionTier: string
}
